import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import Cropper from 'react-easy-crop';
import {
  updateAdminPassword,
  updateAdminProfile,
  uploadAdminAvatar,
  deleteAdminAvatar
} from '../api/adminApi';
import { resolveMediaUrl } from '../utils/media';

const Page = styled.div`
  min-height: 100vh;
  height: 100vh;
  background: radial-gradient(circle at top, #e0e7ff 0%, #eef1f6 45%, #f8fafc 100%);
  padding: 48px 24px;
  font-family: 'Space Grotesk', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow-y: auto;
`;

const Card = styled.div`
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
  border-radius: 36px;
  padding: 48px;
  box-shadow: 0 35px 120px rgba(15, 23, 42, 0.16);
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`;

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const Avatar = styled.div`
  width: 82px;
  height: 82px;
  border-radius: 28px;
  background: #0f172a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BackButton = styled.button`
  border: none;
  background: transparent;
  color: #0f172a;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const CTAGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const OutlineButton = styled.button`
  border: 1px solid #cbd5f5;
  background: transparent;
  color: #0f172a;
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0f172a;
    transform: translateY(-1px);
  }
`;

const LogoutButton = styled.button`
  border: none;
  background: #111322;
  color: #fff;
  padding: 11px 22px;
  border-radius: 999px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 32px rgba(17, 19, 34, 0.3);
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 18px;
`;

const DetailCard = styled.div`
  background: #f8fafc;
  border-radius: 22px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DetailLabel = styled.span`
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const DetailValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
`;

const EmptyState = styled.div`
  padding: 24px;
  border-radius: 20px;
  background: #0f172a;
  color: #cbd5f5;
  text-align: center;
`;

const UploadPanel = styled.div`
  border: 1px dashed #cbd5f5;
  border-radius: 28px;
  padding: 24px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UploadHint = styled.p`
  margin: 0;
  color: #475467;
  font-size: 14px;
`;

const UploadActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const UploadButton = styled.button`
  border: none;
  background: linear-gradient(120deg, #111322, #1f2937);
  color: #fff;
  padding: 12px 20px;
  border-radius: 999px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 38px rgba(15, 23, 42, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const RemoveButton = styled.button`
  border: 1px solid #e11d48;
  background: transparent;
  color: #e11d48;
  padding: 12px 18px;
  border-radius: 999px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(225, 29, 72, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: transparent;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FormSection = styled.form`
  background: #f8fafc;
  border-radius: 28px;
  padding: 28px;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  color: #0f172a;
`;

const SectionDescription = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 14px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #94a3b8;
`;

const Input = styled.input`
  border: 1px solid #cbd5f5;
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 15px;
  outline: none;
  transition: border 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #0f172a;
    box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.1);
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const FormButton = styled.button`
  border: none;
  background: linear-gradient(120deg, #0f172a, #1e293b);
  color: #fff;
  padding: 12px 24px;
  border-radius: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CropOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1000;
`;

const CropPanel = styled.div`
  background: #fff;
  border-radius: 32px;
  width: min(520px, 100%);
  padding: 28px;
  box-shadow: 0 45px 85px rgba(15, 23, 42, 0.35);
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const CropTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  color: #0f172a;
`;

const CropViewport = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  background: #111322;
  border-radius: 24px;
  overflow: hidden;
`;

const CropControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SliderLabel = styled.span`
  font-size: 13px;
  color: #475467;
`;

const SliderInput = styled.input`
  width: 100%;
  accent-color: #0f172a;
`;

const CropActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SecondaryAction = styled.button`
  flex: 1;
  min-width: 120px;
  border: 1px solid #cbd5f5;
  background: transparent;
  color: #0f172a;
  padding: 12px 18px;
  border-radius: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: border 0.2s ease, color 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #0f172a;
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryAction = styled.button`
  flex: 1;
  min-width: 160px;
  border: none;
  background: linear-gradient(120deg, #111322, #1f2937);
  color: #fff;
  padding: 12px 24px;
  border-radius: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 15px 30px rgba(17, 19, 34, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

const getCroppedImage = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return canvas.toDataURL('image/png');
};

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const MAX_AVATAR_FILE_SIZE_MB = 2;
const MAX_AVATAR_FILE_SIZE = MAX_AVATAR_FILE_SIZE_MB * 1024 * 1024;

const Profile = ({ admin, avatar, onBack, onLogout, onAvatarChange, onProfileUpdate }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [cropSource, setCropSource] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [profileForm, setProfileForm] = useState({
    full_name: admin?.full_name || '',
    username: admin?.username || '',
    email: admin?.email || ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const fullName = admin?.full_name?.trim() || admin?.username || 'Admin';
  const avatarSrc = useMemo(() => resolveMediaUrl(avatar || admin?.image_url), [avatar, admin?.image_url]);
  const hasAvatar = Boolean(avatar || admin?.image_url);

  const initials = useMemo(() => {
    if (admin?.full_name) {
      return admin.full_name
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    }

    if (admin?.username) {
      return admin.username.slice(0, 2).toUpperCase();
    }

    return 'AD';
  }, [admin]);

  const formatDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
  };

  const profileDetails = useMemo(() => {
    return [
      { label: 'Full name', value: fullName },
      { label: 'Username', value: admin?.username },
      { label: 'Email', value: admin?.email },
      { label: 'Role', value: admin?.role },
      { label: 'Phone', value: admin?.phone },
      { label: 'Created', value: formatDate(admin?.created_at) },
      { label: 'Last login', value: formatDate(admin?.last_login) }
    ].filter((detail) => detail.value);
  }, [admin, fullName]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  const handleLogout = () => {
    if (!onLogout) {
      return;
    }

    Swal.fire({
      title: 'Sign out?',
      text: 'You will be returned to the login screen.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111322',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Log out'
    }).then((result) => {
      if (result.isConfirmed) {
        onLogout();
        Swal.fire({
          title: 'Signed out',
          text: 'Session closed securely.',
          icon: 'success',
          timer: 1200,
          showConfirmButton: false
        });
      }
    });
  };

  const triggerFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_AVATAR_FILE_SIZE) {
      Swal.fire({
        icon: 'error',
        title: 'Image too large',
        text: `Choose an image under ${MAX_AVATAR_FILE_SIZE_MB}MB.`,
        confirmButtonColor: '#111322'
      });
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      event.target.value = '';
      if (typeof reader.result === 'string') {
        setCropSource(reader.result);
        setIsCropping(true);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      }
    };
    reader.onerror = () => {
      event.target.value = '';
      Swal.fire({
        icon: 'error',
        title: 'Upload failed',
        text: 'Please try again with a different image.',
        confirmButtonColor: '#111322'
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = () => {
    if (!admin?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Missing admin ID',
        text: 'Reload and try again.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    Swal.fire({
      title: 'Remove avatar?',
      text: 'Your current profile image will be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Remove image'
    }).then(async (result) => {
      if (!result.isConfirmed) {
        return;
      }

      try {
        setUploading(true);
        await deleteAdminAvatar(admin.id);
        if (onAvatarChange) {
          onAvatarChange(null);
        }
        if (onProfileUpdate) {
          onProfileUpdate({ ...admin, image_url: null });
        }
        Swal.fire({
          icon: 'success',
          title: 'Avatar removed',
          timer: 1200,
          showConfirmButton: false
        });
      } catch (error) {
        const message = error?.response?.data?.message || 'Unable to remove avatar.';
        Swal.fire({
          icon: 'error',
          title: 'Remove failed',
          text: message,
          confirmButtonColor: '#111322'
        });
      } finally {
        setUploading(false);
      }
    });
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();

    if (!admin?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Missing admin ID',
        text: 'Reload and try again.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    const trimmedFullName = profileForm.full_name.trim();
    const trimmedUsername = profileForm.username.trim();
    const trimmedEmail = profileForm.email.trim();

    if (!trimmedUsername || !trimmedEmail) {
      Swal.fire({
        icon: 'warning',
        title: 'Required fields',
        text: 'Username and email cannot be empty.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid email',
        text: 'Enter a valid email address.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    const updates = {};
    if (trimmedFullName !== (admin?.full_name || '')) {
      updates.full_name = trimmedFullName;
    }
    if (trimmedUsername !== (admin?.username || '')) {
      updates.username = trimmedUsername;
    }
    if (trimmedEmail !== (admin?.email || '')) {
      updates.email = trimmedEmail;
    }

    if (Object.keys(updates).length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No changes detected',
        text: 'Update a field before saving.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    try {
      setIsSavingProfile(true);
      await updateAdminProfile(admin.id, updates);
      const updatedAdmin = { ...admin, ...updates };
      if (onProfileUpdate) {
        onProfileUpdate(updatedAdmin);
      }
      Swal.fire({
        icon: 'success',
        title: 'Profile updated',
        timer: 1600,
        showConfirmButton: false
      });
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to update profile.';
      Swal.fire({
        icon: 'error',
        title: 'Update failed',
        text: message,
        confirmButtonColor: '#111322'
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (!admin?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Missing admin ID',
        text: 'Reload and try again.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword || !newPassword || !confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Fill in all fields',
        text: 'Provide current, new, and confirmation passwords.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    if (newPassword.length < 8) {
      Swal.fire({
        icon: 'warning',
        title: 'Password too short',
        text: 'New password must be at least 8 characters.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Passwords do not match',
        text: 'Ensure the new password and confirmation match.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    if (newPassword === currentPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'No change detected',
        text: 'Choose a different password than the current one.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    try {
      setIsSavingPassword(true);
      await updateAdminPassword(admin.id, { currentPassword, newPassword });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      Swal.fire({
        icon: 'success',
        title: 'Password updated',
        timer: 1600,
        showConfirmButton: false
      });
    } catch (error) {
      const message = error?.response?.data?.message || 'Unable to update password.';
      Swal.fire({
        icon: 'error',
        title: 'Update failed',
        text: message,
        confirmButtonColor: '#111322'
      });
    } finally {
      setIsSavingPassword(false);
    }
  };

  useEffect(() => {
    setProfileForm({
      full_name: admin?.full_name || '',
      username: admin?.username || '',
      email: admin?.email || ''
    });
  }, [admin]);

  const handleProfileInputChange = (field) => (event) => {
    setProfileForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handlePasswordInputChange = (field) => (event) => {
    setPasswordForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCropCancel = () => {
    setIsCropping(false);
    setCropSource(null);
    setCroppedAreaPixels(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleCropSave = async () => {
    if (!croppedAreaPixels || !cropSource) {
      return;
    }

    if (!admin?.id) {
      Swal.fire({
        icon: 'error',
        title: 'Missing admin ID',
        text: 'Reload and try again.',
        confirmButtonColor: '#111322'
      });
      return;
    }

    try {
      setUploading(true);
      const croppedDataUrl = await getCroppedImage(cropSource, croppedAreaPixels);
      const blob = await dataUrlToBlob(croppedDataUrl);
      const extension = blob.type?.split('/')?.[1] || 'png';
      const formData = new FormData();
      formData.append('avatar', blob, `avatar-${admin.id}.${extension}`);
      const { data } = await uploadAdminAvatar(admin.id, formData);
      const imageUrl = data?.imageUrl || null;

      if (imageUrl) {
        if (onAvatarChange) {
          onAvatarChange(imageUrl);
        }
        if (onProfileUpdate) {
          onProfileUpdate({ ...admin, image_url: imageUrl });
        }
        Swal.fire({
          icon: 'success',
          title: 'Avatar updated',
          timer: 1600,
          showConfirmButton: false
        });
      }

      handleCropCancel();
    } catch (error) {
      const message = error?.response?.data?.message || 'Could not upload avatar.';
      Swal.fire({
        icon: 'error',
        title: 'Upload failed',
        text: message,
        confirmButtonColor: '#111322'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Page>
      <Card>
        <div>
          <BackButton type="button" onClick={handleBack}>
            ← Back to dashboard
          </BackButton>
        </div>

        <HeaderRow>
          <Identity>
            <Avatar>
              {avatarSrc ? <AvatarImage src={avatarSrc} alt={`${fullName} avatar`} /> : initials}
            </Avatar>
            <NameBlock>
              <span style={{ fontSize: '28px', fontWeight: 700, color: '#0f172a' }}>{fullName}</span>
              <span style={{ color: '#64748b' }}>{admin?.email || 'No email on file'}</span>
            </NameBlock>
          </Identity>
          <CTAGroup>
            <OutlineButton type="button" onClick={handleBack}>
              Return
            </OutlineButton>
            <LogoutButton type="button" onClick={handleLogout}>
              Log out
            </LogoutButton>
          </CTAGroup>
        </HeaderRow>

        <UploadPanel>
          <UploadHint>
            Personalize your workspace with a profile image (JPG or PNG, up to {MAX_AVATAR_FILE_SIZE_MB}MB).
          </UploadHint>
          <UploadActions>
            <UploadButton type="button" onClick={triggerFileDialog} disabled={uploading}>
              {uploading ? 'Processing...' : hasAvatar ? 'Update image' : 'Upload image'}
            </UploadButton>
            {hasAvatar && (
              <RemoveButton type="button" onClick={handleRemoveAvatar} disabled={uploading}>
                Remove image
              </RemoveButton>
            )}
          </UploadActions>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
          />
        </UploadPanel>

        <FormSection onSubmit={handleProfileSubmit}>
          <SectionHeader>
            <SectionTitle>Account details</SectionTitle>
            <SectionDescription>Update your name, username, or email address.</SectionDescription>
          </SectionHeader>
          <FieldGrid>
            <Field>
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                type="text"
                value={profileForm.full_name}
                onChange={handleProfileInputChange('full_name')}
                placeholder="Your full name"
                disabled={isSavingProfile}
                autoComplete="name"
              />
            </Field>
            <Field>
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                value={profileForm.username}
                onChange={handleProfileInputChange('username')}
                placeholder="Unique username"
                disabled={isSavingProfile}
                autoComplete="username"
                required
              />
            </Field>
            <Field>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={profileForm.email}
                onChange={handleProfileInputChange('email')}
                placeholder="you@example.com"
                disabled={isSavingProfile}
                autoComplete="email"
                required
              />
            </Field>
          </FieldGrid>
          <FormActions>
            <FormButton type="submit" disabled={isSavingProfile}>
              {isSavingProfile ? 'Saving...' : 'Save changes'}
            </FormButton>
          </FormActions>
        </FormSection>

        <FormSection onSubmit={handlePasswordSubmit}>
          <SectionHeader>
            <SectionTitle>Security</SectionTitle>
            <SectionDescription>Change your password to keep your account secure.</SectionDescription>
          </SectionHeader>
          <FieldGrid>
            <Field>
              <Label htmlFor="currentPassword">Current password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordForm.currentPassword}
                onChange={handlePasswordInputChange('currentPassword')}
                placeholder="Enter current password"
                disabled={isSavingPassword}
                autoComplete="current-password"
              />
            </Field>
            <Field>
              <Label htmlFor="newPassword">New password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={handlePasswordInputChange('newPassword')}
                placeholder="At least 8 characters"
                disabled={isSavingPassword}
                autoComplete="new-password"
              />
            </Field>
            <Field>
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordInputChange('confirmPassword')}
                placeholder="Repeat new password"
                disabled={isSavingPassword}
                autoComplete="new-password"
              />
            </Field>
          </FieldGrid>
          <FormActions>
            <FormButton type="submit" disabled={isSavingPassword}>
              {isSavingPassword ? 'Updating...' : 'Update password'}
            </FormButton>
          </FormActions>
        </FormSection>

        {profileDetails.length ? (
          <DetailGrid>
            {profileDetails.map((detail) => (
              <DetailCard key={detail.label}>
                <DetailLabel>{detail.label}</DetailLabel>
                <DetailValue>{detail.value}</DetailValue>
              </DetailCard>
            ))}
          </DetailGrid>
        ) : (
          <EmptyState>No additional profile data available.</EmptyState>
        )}
      </Card>

      {isCropping && cropSource && (
        <CropOverlay role="dialog" aria-modal="true">
          <CropPanel>
            <CropTitle>Adjust avatar</CropTitle>
            <CropViewport>
              <Cropper
                image={cropSource}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </CropViewport>
            <CropControls>
              <SliderLabel>Zoom: {zoom.toFixed(2)}x</SliderLabel>
              <SliderInput
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={zoom}
                onChange={(event) => setZoom(Number(event.target.value))}
              />
            </CropControls>
            <CropActions>
              <SecondaryAction type="button" onClick={handleCropCancel} disabled={uploading}>
                Cancel
              </SecondaryAction>
              <PrimaryAction
                type="button"
                onClick={handleCropSave}
                disabled={!croppedAreaPixels || uploading}
              >
                {uploading ? 'Saving...' : 'Save avatar'}
              </PrimaryAction>
            </CropActions>
          </CropPanel>
        </CropOverlay>
      )}
    </Page>
  );
};

export default Profile;
